/**
 * Copyright IBM Corp. 2022, 2026
 */

import type { ForwardRefExoticComponent } from 'react'
import { forwardRef } from 'react'
import classNames from 'classnames'
import { ButtonPrimitive, type LegacyButtonColor } from './button-primitive'
import s from './styles.module.scss'
import type { FlightIconName } from '../flight-icon'

export const BUTTON_SIZES = ['small', 'medium', 'large'] as const
export type ButtonSize = (typeof BUTTON_SIZES)[number]

export const BUTTON_ICON_POSITIONS = ['leading', 'trailing'] as const
export type ButtonIconPosition = (typeof BUTTON_ICON_POSITIONS)[number]

export const BUTTON_COLORS = [
	'primary',
	'primary-black',
	'primary-white',
	'secondary', // deprecated; maps to secondary-white
	'secondary-high-contrast', // alternates based on theme
	'secondary-white',
	'secondary-black',
	'tertiary',
	'critical',
	'boundary',
	'consul',
	'nomad',
	'packer',
	'terraform',
	'vagrant',
	'vault',
	'waypoint',
	'hashicorp', // alias to primary
] as const
export type ButtonColor = (typeof BUTTON_COLORS)[number]

export interface ButtonPrimitiveProps {
	size?: ButtonSize
	color?: LegacyButtonColor
	isFullWidth?: boolean
	isIconOnly?: boolean
	icon?: FlightIconName
	iconPosition?: ButtonIconPosition
	text?: string
	href?: string
	isHrefExternal?: boolean
	onClick?: React.MouseEventHandler<HTMLButtonElement>
	className?: string
	locale?: string
	disabled?: boolean
	autoFocus?: boolean
	type?: 'button' | 'submit' | 'reset'
}

// map HDS “super-set” colors down to our legacy ones
const COLOR_MAP: Record<ButtonColor, LegacyButtonColor> = {
	primary: 'primary',
	'primary-black': 'custom',
	'primary-white': 'custom',
	secondary: 'custom',
	'secondary-high-contrast': 'custom',
	'secondary-white': 'custom',
	'secondary-black': 'custom',
	tertiary: 'tertiary',
	critical: 'critical',
	boundary: 'custom',
	consul: 'custom',
	nomad: 'custom',
	packer: 'custom',
	terraform: 'custom',
	vagrant: 'custom',
	vault: 'custom',
	waypoint: 'custom',
	hashicorp: 'primary',
}

export interface ButtonProps extends Omit<ButtonPrimitiveProps, 'color'> {
	color?: ButtonColor
}

export const Button = forwardRef<
	HTMLAnchorElement | HTMLButtonElement,
	ButtonProps
>(({ color = 'primary', className, ...rest }, ref) => {
	const legacyColor = COLOR_MAP[color]

	const remappedColors: Partial<Record<ButtonColor, ButtonColor>> = {
		hashicorp: 'primary',
		secondary: 'secondary-white',
	}

	const customClass =
		legacyColor === 'custom'
			? s[`color-${remappedColors[color] ?? color}`]
			: undefined

	return (
		<ButtonPrimitive
			{...rest}
			color={legacyColor}
			className={classNames(customClass, className)}
			ref={ref as ForwardRefExoticComponent<HTMLButtonElement>}
		/>
	)
})

Button.displayName = 'Button'
